// apps/web/src/features/crm/leads/activity/components/LeadTasksSection.tsx
//
// Drawer-embedded list of a lead's OPEN tasks (#1804), re-skinned into the
// `SectionCard` rhythm (#2315, reference `lead-tasks.jsx` TasksBlock). A
// manager with `tasks.manage` can create a free-form MANUAL self-task, and
// reschedule or complete their own MANUAL rows; auto rows are read-only. The
// capability is computed locally (NOT inherited from the lead's
// `leads.manage`) because task affordances gate on `tasks.manage`, a
// distinct permission.
//
// The `/leads` route only requires `leads.read`, so a leads.read-only actor
// can reach this section even though `GET /lead-tasks` is gated on
// `tasks.read`. We therefore gate the section on `tasks.read`: without it we
// render nothing AND keep the query disabled, so a read-only role never fires
// a 403-bound request. This is the single owner of the gate — the parent Pane
// always mounts this section, so the guard is reachable in production.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LeadTaskKind, type LeadTask } from '@magimp/shared';
import { useLeadTasks } from '@magimp/shared-hooks';
import {
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  SectionCard,
} from '../../../../../ui/index.js';
import { hasPermission, useAuthStore } from '../../../../../stores/auth.js';
import { errorMessage } from '../../../../../api/errors.js';
import { LeadTaskRow } from './LeadTaskRow.js';
import { LeadTaskEditorDrawer } from './LeadTaskEditorDrawer.js';
import { LeadCancelledTasksSubsection } from './LeadCancelledTasksSubsection.js';

export interface LeadTasksSectionProps {
  leadId: string;
  /**
   * #2317: admin-or-assigned card editability gate. Unlocks the per-row CANCEL
   * control and the collapsed cancelled-tasks subsection. Optional so existing
   * single-prop mounts (drawer/episode) keep read-only behavior — a pool /
   * non-owner viewer never sees a cancel/reopen affordance.
   */
  canEditCard?: boolean;
}

// Closed | create-new | reschedule-existing.
type EditorState = { mode: 'closed' } | { mode: 'create' } | { mode: 'edit'; task: LeadTask };

export function LeadTasksSection({ leadId, canEditCard = false }: LeadTasksSectionProps) {
  const { t } = useTranslation(['leads', 'common']);
  const perms = useAuthStore((s) => s.permissions);
  const currentUserId = useAuthStore((s) => s.user?.id) ?? null;
  const isAdmin = perms === 'ALL';
  const canReadTasks = isAdmin || hasPermission(perms, 'tasks.read');
  const canManageTasks = isAdmin || hasPermission(perms, 'tasks.manage');

  const tasksQ = useLeadTasks(leadId, { enabled: canReadTasks });
  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });

  if (!canReadTasks) return null;

  const items = tasksQ.data?.items ?? [];
  // WHY (D-1): overdue is scoped to MANUAL rows only — an auto STAGE_PRIMARY/
  // CALLBACK_URGENT row carries no manager-facing "overdue" affordance (it is
  // read-only and system-managed), so it never contributes to the count.
  const overdueCount = items.filter(
    (task) =>
      task.kind === LeadTaskKind.MANUAL &&
      task.dueAt !== null &&
      new Date(task.dueAt).getTime() < Date.now(),
  ).length;

  const title = (
    <>
      {t('leads:tasks.title')}
      {tasksQ.isSuccess && (
        <span
          style={{
            marginLeft: 8,
            fontWeight: 400,
            textTransform: 'none',
            letterSpacing: 'normal',
            fontSize: 'var(--fz-12)',
            color: 'var(--fg-3)',
          }}
        >
          {t('leads:tasks.openCount', { count: items.length })}
          {overdueCount > 0 && (
            <span style={{ color: 'var(--status-error-fg)', fontWeight: 500 }}>
              {' '}
              · {t('leads:tasks.overdueCount', { count: overdueCount })}
            </span>
          )}
        </span>
      )}
    </>
  );

  const createAction = canManageTasks ? (
    <Button
      kind="ghost"
      size="sm"
      icon="plus"
      onClick={() => setEditor({ mode: 'create' })}
      data-testid="lead-tasks-create"
    >
      {t('leads:tasks.create')}
    </Button>
  ) : null;

  return (
    <div data-testid="lead-tasks-section">
      <SectionCard title={title} action={createAction}>
        {tasksQ.isPending && <LoadingState />}
        {tasksQ.error && (
          <ErrorState
            title={t('common:states.loadError')}
            text={errorMessage(tasksQ.error)}
            onRetry={() => void tasksQ.refetch()}
          />
        )}
        {tasksQ.isSuccess &&
          (items.length === 0 ? (
            <EmptyState icon="package" title={t('leads:tasks.empty')} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map((task) => (
                <LeadTaskRow
                  key={task.id}
                  task={task}
                  canManage={canManageTasks}
                  canEditCard={canEditCard}
                  isAdmin={isAdmin}
                  currentUserId={currentUserId}
                  onReschedule={(target) => setEditor({ mode: 'edit', task: target })}
                />
              ))}
            </div>
          ))}
        {/* #2317 D-3a: collapsed cancelled-tasks subsection, editor-only (a
            reopen needs tasks.manage, so gate on canEditCard AND canManageTasks). */}
        <LeadCancelledTasksSubsection leadId={leadId} enabled={canEditCard && canManageTasks} />
      </SectionCard>
      {editor.mode === 'create' && (
        <LeadTaskEditorDrawer leadId={leadId} onClose={() => setEditor({ mode: 'closed' })} />
      )}
      {editor.mode === 'edit' && (
        <LeadTaskEditorDrawer
          leadId={leadId}
          task={editor.task}
          onClose={() => setEditor({ mode: 'closed' })}
        />
      )}
    </div>
  );
}
